/**
 * ============================================================================
 * CANVAS RENDERER UTILITY: High-Fidelity Dynamic Image Generator
 * ============================================================================
 * Purpose:
 *   Renders dynamic image templates onto HTML5 canvas with substituted
 *   variables (e.g. {{name}}, {{offer}}, {{code}}), custom typography,
 *   background images, and overlay images.
 * ============================================================================
 */

// Cache for preloaded HTML images to avoid repeated network requests
const imageCache = new Map();

/**
 * Resolves image URL to an accessible URL (handles localhost dev rewrite, relative paths, etc.)
 */
export const resolveImageUrl = (rawUrl) => {
  if (!rawUrl) return "";
  const trimmed = String(rawUrl).trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:") || /\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/.test(trimmed)) {
    return trimmed;
  }

  const isLocal =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "0.0.0.0");

  // In local dev, if URL points to apis.digiforest.in/uploads, rewrite to local backend http://localhost:8005/uploads
  if (isLocal && (trimmed.includes("apis.digiforest.in/uploads/") || trimmed.includes("panel.digiforest.in/uploads/"))) {
    return trimmed
      .replace("https://apis.digiforest.in", "http://localhost:8005")
      .replace("http://apis.digiforest.in", "http://localhost:8005")
      .replace("https://panel.digiforest.in", "http://localhost:8005")
      .replace("http://panel.digiforest.in", "http://localhost:8005");
  }

  // Handle relative upload paths like /uploads/dynamic/...
  if (trimmed.startsWith("/uploads/") || trimmed.startsWith("uploads/")) {
    const base = isLocal ? "http://localhost:8005" : (process.env.REACT_APP_SERVER_URL || "https://apis.digiforest.in");
    return `${base.replace(/\/+$/, "")}/${trimmed.replace(/^\/+/, "")}`;
  }

  return trimmed;
};

/**
 * Preloads image with anonymous CORS support and safe fallback
 */
export const preloadImage = (rawUrl) => {
  if (!rawUrl) return Promise.resolve(null);
  const url = resolveImageUrl(rawUrl);
  if (imageCache.has(url)) {
    const cached = imageCache.get(url);
    if (cached && (cached.complete || cached.naturalWidth > 0)) {
      return Promise.resolve(cached);
    }
  }

  // If url contains un-substituted template placeholders like {{qr_code}}
  if (/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/.test(url)) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const img = new Image();
    const isDataOrBlob = url.startsWith("data:") || url.startsWith("blob:");

    // Only set crossOrigin for remote http(s) URLs
    if (!isDataOrBlob) {
      img.crossOrigin = "anonymous";
    }

    img.onload = () => {
      imageCache.set(url, img);
      resolve(img);
    };

    img.onerror = () => {
      // If anonymous CORS failed on external URL, retry without crossOrigin so it displays on canvas
      if (img.crossOrigin) {
        const fallback = new Image();
        fallback.onload = () => {
          imageCache.set(url, fallback);
          resolve(fallback);
        };
        fallback.onerror = () => resolve(null);
        fallback.src = url;
      } else {
        resolve(null);
      }
    };

    img.src = url;

    // Safety check if already complete in cache
    if (img.complete && img.naturalWidth !== 0) {
      imageCache.set(url, img);
      resolve(img);
    }
  });
};

/**
 * Replaces {{varName}} placeholders inside text using variableValues
 */
export const replacePlaceholders = (text, variableValues = {}) => {
  if (!text) return "";
  return String(text).replace(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g, (match, key) => {
    return variableValues[key] !== undefined && variableValues[key] !== null
      ? String(variableValues[key])
      : match;
  });
};

/**
 * Extracts all unique {{varName}} variable names from template text layers and image layers
 */
export const extractTemplateVariables = (template) => {
  const vars = new Set();
  if (!template) return [];

  const layers = Array.isArray(template.layers)
    ? template.layers
    : Array.isArray(template.arr_text_boxes)
    ? template.arr_text_boxes
    : [];

  layers.forEach((layer) => {
    const textVal = layer.text || "";
    if (textVal) {
      const matches = textVal.match(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g);
      if (matches) {
        matches.forEach((m) => {
          const varName = m.replace(/[\{\}\s]/g, "");
          if (varName) vars.add(varName);
        });
      }
    }
    const urlVal = layer.url || "";
    if (urlVal) {
      const matches = urlVal.match(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g);
      if (matches) {
        matches.forEach((m) => {
          const varName = m.replace(/[\{\}\s]/g, "");
          if (varName) vars.add(varName);
        });
      }
    }
  });

  return Array.from(vars);
};

/**
 * Draws rounded rectangle path onto canvas context
 */
export const drawRoundedRect = (ctx, x, y, width, height, radius = 0) => {
  if (!radius || radius <= 0) {
    ctx.rect(x, y, width, height);
    return;
  }
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.arcTo(x + width, y, x + width, y + r, r);
  ctx.lineTo(x + width, y + height - r);
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
  ctx.lineTo(x + r, y + height);
  ctx.arcTo(x, y + height, x, y + height - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
};

// Preset Frame Styles for Dynamic WhatsApp Image Campaigns
export const FRAME_PRESETS = [
  { id: "NONE", label: "None", icon: "🚫", description: "Clean image with no frame" },
  { id: "LUXURY_GOLD_WAVE", label: "Royal Wave Frame", icon: "⚜️", description: "Gold frame with black & gold wave ribbon corners", defaultAsset: "/frames/luxury_gold_wave.png" },
  { id: "POLAROID_TRIO", label: "Polaroid Collage 3", icon: "📸", description: "Tilted trio of Polaroid photo frames with cutouts", defaultAsset: "/frames/polaroid_tilted_trio.png" },
  { id: "POLAROID_GRID", label: "Polaroid Trio Grid", icon: "🎞️", description: "Side-by-side Polaroid gallery frames", defaultAsset: "/frames/polaroid_grid_trio.png" },
  { id: "POLAROID", label: "Polaroid Classic", icon: "📷", description: "Single instant camera photo card with caption space" },
  { id: "GOLD_LUXURY", label: "Royal Gold", icon: "👑", description: "Metallic gold gradient & corner gems" },
  { id: "SOLID_BORDER", label: "Classic Solid", icon: "🔲", description: "Clean customizable solid border" },
  { id: "DOUBLE_BORDER", label: "Luxury Double", icon: "🏛️", description: "Two-tone luxury double line border" },
  { id: "RIBBONS", label: "Festive Ribbons", icon: "🎀", description: "Celebratory diagonal corner & bottom ribbons" },
  { id: "BOTTOM_BAND", label: "Brand Band", icon: "🏷️", description: "Modern lower-third banner for contact/address" },
  { id: "CORNERS", label: "Modern Corners", icon: "📐", description: "Geometric L-brackets in corners" },
  { id: "NEON_GLOW", label: "Neon Glow", icon: "⚡", description: "Luminous cyber neon glow frame" },
  { id: "VINTAGE_STAMP", label: "Vintage Stamp", icon: "✉️", description: "Perforated certificate / stamp border" },
  { id: "CUSTOM_PNG", label: "Custom PNG", icon: "🖼️", description: "Upload custom transparent frame overlay" },
];

/**
 * Draws frame decorations onto canvas context
 */
export const renderFrame = async (ctx, width, height, frameConfig = {}, targetRect = null) => {
  const type = (frameConfig.type || "NONE").toUpperCase();
  if (!type || type === "NONE") return;

  const position = (frameConfig.position || "INSIDE").toUpperCase();
  const isOutside = position === "OUTSIDE";
  const thickness = Math.max(2, Number(frameConfig.width) || 24);
  const color = frameConfig.color || "#b91c1c";
  const accentColor = frameConfig.accentColor || "#f59e0b";
  const customUrl = frameConfig.customUrl || "";

  // Frame width & height adjustments (scale % from 10% to 250%) and X/Y offsets
  const frameScaleX = Math.max(10, Math.min(250, Number(frameConfig.scaleX ?? frameConfig.scale ?? 100)));
  const frameScaleY = Math.max(10, Math.min(250, Number(frameConfig.scaleY ?? frameConfig.scale ?? 100)));
  const frameOffsetX = Number(frameConfig.offsetX) || 0;
  const frameOffsetY = Number(frameConfig.offsetY) || 0;

  // The base area: canvas when OUTSIDE, photo rect when INSIDE
  const baseRect = (isOutside ? { x: 0, y: 0, width, height } : targetRect) || { x: 0, y: 0, width, height };

  // Calculate adjusted rect based on scale and offset
  const adjW = Math.round((baseRect.width * frameScaleX) / 100);
  const adjH = Math.round((baseRect.height * frameScaleY) / 100);
  const adjX = Math.round(baseRect.x + (baseRect.width - adjW) / 2 + frameOffsetX);
  const adjY = Math.round(baseRect.y + (baseRect.height - adjH) / 2 + frameOffsetY);
  const rect = { x: adjX, y: adjY, width: adjW, height: adjH };

  ctx.save();

  switch (type) {
    case "LUXURY_GOLD_WAVE": {
      const assetUrl = customUrl || "/frames/luxury_gold_wave.png";
      const frameImg = await preloadImage(assetUrl);
      if (frameImg) {
        ctx.drawImage(frameImg, rect.x, rect.y, rect.width, rect.height);
      } else {
        // High-fidelity procedural fallback for Royal Wave Frame
        const goldW = Math.max(4, Math.round(thickness * 0.45));
        ctx.strokeStyle = "#D4AF37";
        ctx.lineWidth = goldW;
        ctx.strokeRect(rect.x + goldW / 2, rect.y + goldW / 2, rect.width - goldW, rect.height - goldW);
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x + goldW * 2, rect.y + goldW * 2, rect.width - goldW * 4, rect.height - goldW * 4);
      }
      break;
    }

    case "POLAROID_TRIO": {
      const assetUrl = customUrl || "/frames/polaroid_tilted_trio.png";
      const frameImg = await preloadImage(assetUrl);
      if (frameImg) {
        ctx.drawImage(frameImg, rect.x, rect.y, rect.width, rect.height);
      }
      break;
    }

    case "POLAROID_GRID": {
      const assetUrl = customUrl || "/frames/polaroid_grid_trio.png";
      const frameImg = await preloadImage(assetUrl);
      if (frameImg) {
        ctx.drawImage(frameImg, rect.x, rect.y, rect.width, rect.height);
      }
      break;
    }

    case "SOLID_BORDER":
    case "BORDER": {
      ctx.strokeStyle = color;
      ctx.lineWidth = thickness;
      if (isOutside) {
        const half = thickness / 2;
        ctx.strokeRect(rect.x - half, rect.y - half, rect.width + thickness, rect.height + thickness);
      } else {
        const half = thickness / 2;
        ctx.strokeRect(rect.x + half, rect.y + half, rect.width - thickness, rect.height - thickness);
      }
      break;
    }

    case "DOUBLE_BORDER": {
      const outerW = Math.max(2, Math.round(thickness * 0.5));
      const innerW = Math.max(1.5, Math.round(thickness * 0.22));
      const gap = Math.max(3, Math.round(thickness * 0.28));

      if (isOutside) {
        ctx.strokeStyle = color;
        ctx.lineWidth = outerW;
        ctx.strokeRect(outerW / 2, outerW / 2, width - outerW, height - outerW);

        ctx.strokeStyle = accentColor;
        ctx.lineWidth = innerW;
        ctx.strokeRect(rect.x - gap, rect.y - gap, rect.width + gap * 2, rect.height + gap * 2);
      } else {
        ctx.strokeStyle = color;
        ctx.lineWidth = outerW;
        ctx.strokeRect(rect.x + outerW / 2, rect.y + outerW / 2, rect.width - outerW, rect.height - outerW);

        const inset = outerW + gap;
        ctx.strokeStyle = accentColor;
        ctx.lineWidth = innerW;
        ctx.strokeRect(rect.x + inset, rect.y + inset, rect.width - inset * 2, rect.height - inset * 2);
      }
      break;
    }

    case "GOLD_LUXURY": {
      const goldGrad = ctx.createLinearGradient(0, 0, width, height);
      goldGrad.addColorStop(0, "#BF953F");
      goldGrad.addColorStop(0.25, "#FCF6BA");
      goldGrad.addColorStop(0.5, "#B38728");
      goldGrad.addColorStop(0.75, "#FBF5B7");
      goldGrad.addColorStop(1, "#AA771C");

      const goldW = Math.max(3, Math.round(thickness * 0.45));
      const inset = Math.max(6, Math.round(thickness * 0.6));

      ctx.strokeStyle = goldGrad;
      ctx.lineWidth = goldW;

      if (isOutside) {
        ctx.strokeRect(rect.x - 4, rect.y - 4, rect.width + 8, rect.height + 8);
        ctx.strokeRect(goldW / 2, goldW / 2, width - goldW, height - goldW);
      } else {
        ctx.strokeRect(rect.x + goldW / 2, rect.y + goldW / 2, rect.width - goldW, rect.height - goldW);
        ctx.strokeStyle = "rgba(251, 245, 183, 0.75)";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(rect.x + inset, rect.y + inset, rect.width - inset * 2, rect.height - inset * 2);
      }

      const cornerSize = Math.max(10, Math.round(thickness * 0.65));
      const drawDiamond = (cx, cy) => {
        ctx.save();
        ctx.fillStyle = goldGrad;
        ctx.beginPath();
        ctx.moveTo(cx, cy - cornerSize);
        ctx.lineTo(cx + cornerSize, cy);
        ctx.lineTo(cx, cy + cornerSize);
        ctx.lineTo(cx - cornerSize, cy);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      };

      const cornerOffset = isOutside ? Math.max(14, thickness * 0.8) : inset;
      drawDiamond(rect.x + cornerOffset, rect.y + cornerOffset);
      drawDiamond(rect.x + rect.width - cornerOffset, rect.y + cornerOffset);
      drawDiamond(rect.x + cornerOffset, rect.y + rect.height - cornerOffset);
      drawDiamond(rect.x + rect.width - cornerOffset, rect.y + rect.height - cornerOffset);
      break;
    }

    case "POLAROID": {
      ctx.strokeStyle = "rgba(0, 0, 0, 0.18)";
      ctx.lineWidth = 2;
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);

      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      ctx.lineWidth = 1;
      ctx.strokeRect(rect.x - 1, rect.y - 1, rect.width + 2, rect.height + 2);
      break;
    }

    case "RIBBONS": {
      const ribbonSize = Math.max(140, Math.min(width, height) * 0.22);
      ctx.save();
      const ribGrad = ctx.createLinearGradient(0, 0, ribbonSize, ribbonSize);
      ribGrad.addColorStop(0, color);
      ribGrad.addColorStop(1, accentColor);

      ctx.fillStyle = ribGrad;
      ctx.beginPath();
      ctx.moveTo(rect.x, rect.y);
      ctx.lineTo(rect.x + ribbonSize, rect.y);
      ctx.lineTo(rect.x, rect.y + ribbonSize);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(rect.x + ribbonSize - 8, rect.y + 2);
      ctx.lineTo(rect.x + 2, rect.y + ribbonSize - 8);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = "#ffffff";
      ctx.font = `bold ${Math.max(13, Math.round(ribbonSize / 9))}px "Outfit", sans-serif`;
      ctx.textAlign = "center";
      ctx.save();
      ctx.translate(rect.x + ribbonSize * 0.32, rect.y + ribbonSize * 0.32);
      ctx.rotate(-Math.PI / 4);
      ctx.fillText("★ SPECIAL ★", 0, 0);
      ctx.restore();
      ctx.restore();

      const botH = Math.max(14, Math.round(thickness * 0.5));
      ctx.fillStyle = color;
      ctx.fillRect(rect.x, rect.y + rect.height - botH, rect.width, botH);
      ctx.fillStyle = accentColor;
      ctx.fillRect(rect.x, rect.y + rect.height - botH - 3, rect.width, 3);
      break;
    }

    case "BOTTOM_BAND": {
      const bandH = Math.max(110, Math.round(height * 0.16));
      const bandY = rect.y + rect.height - bandH;

      const bandGrad = ctx.createLinearGradient(0, bandY, 0, rect.y + rect.height);
      bandGrad.addColorStop(0, color + "E6");
      bandGrad.addColorStop(1, color);

      ctx.fillStyle = bandGrad;
      ctx.fillRect(rect.x, bandY, rect.width, bandH);

      ctx.fillStyle = accentColor;
      ctx.fillRect(rect.x, bandY, rect.width, 4);
      break;
    }

    case "CORNERS": {
      const armLen = Math.max(50, Math.round(Math.min(rect.width, rect.height) * 0.09));
      const cW = Math.max(4, Math.round(thickness * 0.35));
      const offset = isOutside ? Math.max(4, Math.round(thickness * 0.3)) : Math.max(12, Math.round(thickness * 0.5));

      ctx.strokeStyle = color;
      ctx.lineWidth = cW;
      ctx.lineCap = "square";

      const x1 = rect.x + offset;
      const y1 = rect.y + offset;
      const x2 = rect.x + rect.width - offset;
      const y2 = rect.y + rect.height - offset;

      ctx.beginPath();
      ctx.moveTo(x1, y1 + armLen);
      ctx.lineTo(x1, y1);
      ctx.lineTo(x1 + armLen, y1);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x2 - armLen, y1);
      ctx.lineTo(x2, y1);
      ctx.lineTo(x2, y1 + armLen);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x1, y2 - armLen);
      ctx.lineTo(x1, y2);
      ctx.lineTo(x1 + armLen, y2);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(x2 - armLen, y2);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x2, y2 - armLen);
      ctx.stroke();

      ctx.fillStyle = accentColor;
      const dotR = Math.max(3, Math.round(cW * 0.8));
      const drawDot = (dx, dy) => {
        ctx.beginPath();
        ctx.arc(dx, dy, dotR, 0, Math.PI * 2);
        ctx.fill();
      };
      drawDot(x1, y1);
      drawDot(x2, y1);
      drawDot(x1, y2);
      drawDot(x2, y2);
      break;
    }

    case "NEON_GLOW": {
      const neonW = Math.max(3, Math.round(thickness * 0.35));
      const half = neonW / 2;

      ctx.shadowColor = color;
      ctx.shadowBlur = Math.max(16, thickness * 0.9);
      ctx.strokeStyle = color;
      ctx.lineWidth = neonW;
      ctx.strokeRect(rect.x + half, rect.y + half, rect.width - neonW, rect.height - neonW);

      ctx.shadowBlur = 0;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.75)";
      ctx.lineWidth = Math.max(1, neonW * 0.3);
      ctx.strokeRect(rect.x + half, rect.y + half, rect.width - neonW, rect.height - neonW);
      break;
    }

    case "VINTAGE_STAMP": {
      const outerW = Math.max(2, Math.round(thickness * 0.4));
      const pad = Math.max(8, Math.round(thickness * 0.5));

      ctx.strokeStyle = color;
      ctx.lineWidth = outerW;
      ctx.strokeRect(rect.x + outerW / 2, rect.y + outerW / 2, rect.width - outerW, rect.height - outerW);

      ctx.strokeStyle = accentColor;
      ctx.lineWidth = Math.max(1.5, Math.round(thickness * 0.18));
      ctx.setLineDash([8, 6]);
      ctx.strokeRect(rect.x + pad, rect.y + pad, rect.width - pad * 2, rect.height - pad * 2);
      ctx.setLineDash([]);
      break;
    }

    case "CUSTOM_PNG": {
      if (customUrl) {
        const frameImg = await preloadImage(customUrl);
        if (frameImg) {
          ctx.drawImage(frameImg, rect.x, rect.y, rect.width, rect.height);
        }
      }
      break;
    }

    default:
      break;
  }

  ctx.restore();
};

/**
 * Renders the full template onto a given canvas context with dynamic variables
 */
export const renderTemplateToCanvas = async (canvas, template, variableValues = {}) => {
  if (!canvas || !template) return;

  // Await webfonts readiness if supported
  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (_) {}
  }

  const ctx = canvas.getContext("2d");

  const width = template.canvas?.width || template.num_width || 1080;
  const height = template.canvas?.height || template.num_height || 1080;
  canvas.width = width;
  canvas.height = height;

  // Extract Frame and Outside Padding Configuration
  const frameConfig = template.canvas?.frame || {
    type: template.str_frame_type || "NONE",
    position: template.str_frame_position || "INSIDE",
    width: template.canvas?.frame?.width || 24,
    color: template.canvas?.frame?.color || template.str_main_color || "#b91c1c",
    accentColor: template.canvas?.frame?.accentColor || template.str_accent_color || "#f59e0b",
    padding: template.canvas?.padding ?? template.canvas?.frame?.padding ?? 0,
    customUrl: template.canvas?.frame?.customUrl || template.str_frame_custom_url || "",
  };

  const frameType = (frameConfig.type || "NONE").toUpperCase();
  const framePosition = (frameConfig.position || "INSIDE").toUpperCase(); // "INSIDE" or "OUTSIDE"
  const isOutside = framePosition === "OUTSIDE" && frameType !== "NONE";
  const frameThickness = Math.max(2, Number(frameConfig.width) || 24);
  const primaryColor = frameConfig.color || "#b91c1c";

  // Extract 4-Sided Independent Margin & Padding Configuration (Top, Right, Bottom, Left)
  const padTop = Math.max(
    0,
    Number(
      frameConfig.paddingTop ??
      template.canvas?.paddingTop ??
      frameConfig.padding ??
      template.canvas?.padding ??
      0
    )
  );
  const padRight = Math.max(
    0,
    Number(
      frameConfig.paddingRight ??
      template.canvas?.paddingRight ??
      frameConfig.padding ??
      template.canvas?.padding ??
      0
    )
  );
  const padBottom = Math.max(
    0,
    Number(
      frameConfig.paddingBottom ??
      template.canvas?.paddingBottom ??
      frameConfig.padding ??
      template.canvas?.padding ??
      0
    )
  );
  const padLeft = Math.max(
    0,
    Number(
      frameConfig.paddingLeft ??
      template.canvas?.paddingLeft ??
      frameConfig.padding ??
      template.canvas?.padding ??
      0
    )
  );

  // 1. Calculate Target Image Rectangle
  let imgX = 0;
  let imgY = 0;
  let imgW = width;
  let imgH = height;

  ctx.clearRect(0, 0, width, height);

  if (isOutside) {
    // Fill outer canvas frame background
    if (frameType === "POLAROID") {
      ctx.fillStyle = "#f8fafc";
    } else if (frameType === "GOLD_LUXURY" || frameType === "LUXURY_GOLD_WAVE") {
      const outerGold = ctx.createLinearGradient(0, 0, width, height);
      outerGold.addColorStop(0, "#2a2212");
      outerGold.addColorStop(0.5, "#423214");
      outerGold.addColorStop(1, "#1e170b");
      ctx.fillStyle = outerGold;
    } else {
      ctx.fillStyle = primaryColor;
    }
    ctx.fillRect(0, 0, width, height);

    // Compute inner image rectangle respecting individual side padding
    imgX = padLeft + frameThickness;
    imgY = padTop + frameThickness;
    imgW = Math.max(20, width - (padLeft + padRight + 2 * frameThickness));
    const extraBottom = (frameType === "POLAROID" && padBottom === 0) ? Math.max(80, Math.round(frameThickness * 2.8)) : 0;
    imgH = Math.max(20, height - (padTop + padBottom + 2 * frameThickness) - extraBottom);
  } else {
    // INSIDE (in the image) or NO frame:
    // Uses 4-sided margins/padding; if 0 on all sides, image fills 100% full-bleed
    imgX = padLeft;
    imgY = padTop;
    imgW = Math.max(20, width - padLeft - padRight);
    imgH = Math.max(20, height - padTop - padBottom);
  }

  // Draw background color in image area
  const bgColor = template.canvas?.backgroundColor || template.str_bg_color || "#1e293b";
  ctx.fillStyle = bgColor;
  ctx.fillRect(imgX, imgY, imgW, imgH);

  // Draw background image if present
  const bgImage = template.canvas?.backgroundImage || template.str_bg_image_url || "";
  if (bgImage) {
    const bgImg = await preloadImage(bgImage);
    if (bgImg) {
      const imgRatio = bgImg.width / bgImg.height;
      const targetRatio = imgW / imgH;
      let drawW, drawH, drawX, drawY;

      if (imgRatio > targetRatio) {
        drawH = imgH;
        drawW = imgH * imgRatio;
        drawX = imgX + (imgW - drawW) / 2;
        drawY = imgY;
      } else {
        drawW = imgW;
        drawH = imgW / imgRatio;
        drawX = imgX;
        drawY = imgY + (imgH - drawH) / 2;
      }

      ctx.save();
      ctx.beginPath();
      ctx.rect(imgX, imgY, imgW, imgH);
      ctx.clip(); // Ensure image never bleeds outside its allocated area
      ctx.drawImage(bgImg, drawX, drawY, drawW, drawH);
      ctx.restore();
    }
  }

  // Render Frame decorations (if enabled)
  if (frameType !== "NONE") {
    await renderFrame(ctx, width, height, frameConfig, {
      x: imgX,
      y: imgY,
      width: imgW,
      height: imgH,
    });
  }

  // 2. Draw Layers in order
  const layers = Array.isArray(template.layers)
    ? template.layers
    : Array.isArray(template.arr_text_boxes)
    ? template.arr_text_boxes.map((b, i) => ({
        id: b.id || `box_${i}`,
        type: "text",
        text: b.text || "",
        x: Math.round(((b.x || 50) / 100) * width),
        y: Math.round(((b.y || 50) / 100) * height),
        fontSize: b.fontSize || 42,
        fontFamily: b.fontFamily || "Poppins",
        fontWeight: b.fontWeight === "bold" ? 700 : 500,
        color: b.color || "#ffffff",
        textAlign: b.textAlign || "center",
        maxWidth: Math.round(((b.width || 80) / 100) * width),
        hasBgBox: b.hasBgBox,
        bgBoxColor: b.bgBoxColor,
        visible: true,
      }))
    : [];

  for (const layer of layers) {
    if (layer.visible === false) continue;

    ctx.save();
    ctx.globalAlpha = layer.opacity !== undefined ? Number(layer.opacity) : 1;

    if (layer.type === "image") {
      let resolvedUrl = layer.url || "";
      resolvedUrl = replacePlaceholders(resolvedUrl, variableValues);

      const x = Number(layer.x) || 0;
      const y = Number(layer.y) || 0;
      const w = Number(layer.width) || 200;
      const h = Number(layer.height) || 200;
      const r = Number(layer.borderRadius) || 0;

      let img = null;
      if (resolvedUrl) {
        img = await preloadImage(resolvedUrl);
      }

      if (img) {
        if (r > 0) {
          ctx.beginPath();
          drawRoundedRect(ctx, x, y, w, h, r);
          ctx.clip();
        }
        ctx.drawImage(img, x, y, w, h);
      } else {
        // Fallback placeholder rendering when image is loading or unresolved dynamic variable
        ctx.save();
        ctx.fillStyle = "rgba(58, 87, 232, 0.15)";
        if (r > 0) {
          ctx.beginPath();
          drawRoundedRect(ctx, x, y, w, h, r);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, w, h);
        }

        ctx.strokeStyle = "rgba(58, 87, 232, 0.7)";
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 6]);
        if (r > 0) {
          ctx.beginPath();
          drawRoundedRect(ctx, x, y, w, h, r);
          ctx.stroke();
        } else {
          ctx.strokeRect(x, y, w, h);
        }
        ctx.setLineDash([]);

        // Draw icon & label
        ctx.fillStyle = "#ffffff";
        ctx.font = `bold ${Math.min(20, Math.max(13, Math.round(w / 14)))}px "Outfit", sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(resolvedUrl || layer.name || "🖼️ Image Layer", x + w / 2, y + h / 2);
        ctx.restore();
      }
    } else if (layer.type === "text") {
      const rawText = layer.text || "";
      const text = replacePlaceholders(rawText, variableValues);
      const x = Number(layer.x) || 0;
      const y = Number(layer.y) || 0;
      const fontSize = Number(layer.fontSize) || 36;
      const fontFamily = layer.fontFamily || "Inter";
      const fontWeight = layer.fontWeight || 600;
      const isItalic = !!layer.isItalic;
      const color = layer.color || "#ffffff";
      const textAlign = layer.textAlign || "left";
      const maxWidth = Number(layer.maxWidth) || 0;
      const lineHeight = Number(layer.lineHeight) || fontSize * 1.25;

      ctx.font = `${isItalic ? "italic " : ""}${fontWeight} ${fontSize}px "${fontFamily}", -apple-system, sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = textAlign;
      ctx.textBaseline = "top";

      // Shadow options
      if (layer.shadowColor && (Number(layer.shadowBlur) > 0 || layer.hasShadow)) {
        ctx.shadowColor = layer.shadowColor || "rgba(0,0,0,0.45)";
        ctx.shadowBlur = Number(layer.shadowBlur) || 8;
        ctx.shadowOffsetX = Number(layer.shadowOffsetX) || 0;
        ctx.shadowOffsetY = Number(layer.shadowOffsetY) || 2;
      }

      // Stroke/Outline options
      if (layer.strokeColor && Number(layer.strokeWidth) > 0) {
        ctx.strokeStyle = layer.strokeColor;
        ctx.lineWidth = Number(layer.strokeWidth);
      }

      // Compute anchorX according to alignment within the box
      let anchorX = x;
      if (maxWidth > 0) {
        if (textAlign === "center") {
          anchorX = x + maxWidth / 2;
        } else if (textAlign === "right") {
          anchorX = x + maxWidth;
        }
      }

      const drawTextLine = (line, lineX, lineY) => {
        if (layer.hasBgBox && layer.bgBoxColor) {
          ctx.save();
          ctx.fillStyle = layer.bgBoxColor;
          const textW = ctx.measureText(line).width;
          const pad = 8;
          let boxX = lineX;
          if (textAlign === "center") boxX = lineX - textW / 2;
          else if (textAlign === "right") boxX = lineX - textW;
          drawRoundedRect(ctx, boxX - pad, lineY - 2, textW + pad * 2, lineHeight, 6);
          ctx.fill();
          ctx.restore();
        }

        if (layer.strokeColor && Number(layer.strokeWidth) > 0) {
          ctx.strokeText(line, lineX, lineY);
        }
        ctx.fillText(line, lineX, lineY);
      };

      if (maxWidth > 0 && ctx.measureText(text).width > maxWidth) {
        const words = text.split(" ");
        let currentLine = "";
        let currentY = y;

        for (let n = 0; n < words.length; n++) {
          const testLine = currentLine ? `${currentLine} ${words[n]}` : words[n];
          const testWidth = ctx.measureText(testLine).width;
          if (testWidth > maxWidth && n > 0) {
            drawTextLine(currentLine, anchorX, currentY);
            currentLine = words[n];
            currentY += lineHeight;
          } else {
            currentLine = testLine;
          }
        }
        if (currentLine) {
          drawTextLine(currentLine, anchorX, currentY);
        }
      } else {
        // Multi-line support via explicit \n
        const lines = text.split("\n");
        lines.forEach((line, index) => {
          drawTextLine(line, anchorX, y + index * lineHeight);
        });
      }
    }

    ctx.restore();
  }
};

/**
 * Generates Base64 data URL for given template and variableValues
 */
export const generateDataUrl = async (template, variableValues = {}) => {
  const canvas = document.createElement("canvas");
  await renderTemplateToCanvas(canvas, template, variableValues);
  return canvas.toDataURL("image/png");
};

export default {
  preloadImage,
  replacePlaceholders,
  extractTemplateVariables,
  renderTemplateToCanvas,
  renderFrame,
  FRAME_PRESETS,
  generateDataUrl,
};
